import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ReactComponent as TransactionIcon } from '../../assets/icons/sidebar/sidebar-transaction.svg';
import { useAppBranding } from '../../config/branding';
import DefaultApiLimit from '../../config/defaultLimit';
import { powerUnit } from '../../config/enum';
import {
  AvailableTransactions,
  RequestTransactionInterface,
} from '../../interface/transaction-management.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setNotificationDetails } from '../../redux/slice/NotificationSlice';
import { setPageTitle } from '../../redux/slice/PageTitleSlice';
import { setSelectedStation } from '../../redux/slice/StationManagementSidePanelSlice';
import {
  fetchAllTransactions,
  fetchListFilters,
  setSortData,
  setTransactionFilterData,
  updateTransactionFilters,
} from '../../redux/slice/TransactionManagementSlice';
import CustomTable from '../../shared-components/custom-table/CustomTable';
import ModalBox from '../../shared-components/modal-box/ModalBox';
import DeviceViewSidePanel from '../../shared-components/table-side-panel/DeviceViewSidePanel';
import EndConsumerSidePanel from '../../shared-components/table-side-panel/EndConsumerSidePanel';
import StationViewSidePanel from '../../shared-components/table-side-panel/StationViewSidePanel';
import TransactionSidePanel from '../../shared-components/table-side-panel/TransactionSidePanel';
import { GetParametersFromCommonFilters } from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import { exportCSV, exportPDF } from '../../shared-functions/ExportFile';
import { FormatUtcString } from '../../shared-functions/FormatUtcString';
import { CapitalizeFirstLetter } from '../../shared-functions/StringFunctions';
import ToFixedValue from '../../shared-functions/ToFixedValue';
import transactionApi from '../../utils/api/transaction';

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
  header: boolean;
}

/**
 * Transaction management module
 */
function TransactionManagement() {
  const { t } = useTranslation('transactionManagement');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const branding = useAppBranding();
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [searchParameters, setSearchParameters] = useSearchParams();
  const {
    transactions,
    page,
    limit,
    totalPages,
    totalResults,
    loading,
    filters,
    filterListLoading,
  } = useAppSelector((state) => state.transaction);
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: false,
    header: false,
  });
  const { currentFacilityTagsList } = useAppSelector((state) => state.facilityTags);
  const { sortBy, sortField } = useAppSelector((state) => state.transaction);

  /**
   * Handle the fetch data for Api call function
   * @param data
   */
  const fetchData = (data: RequestTransactionInterface) => {
    const selectedFacility = currentFacilityTagsList.find((item) => item.isSelected);

    if (selectedFacility?._id && !loading) {
      dispatch(
        fetchAllTransactions({
          ...data,
        }),
      );
    }
  };

  /**
   * Function to open device view model
   */
  const handleOpenModelView = (chargingStationDate: AvailableTransactions, newTab: boolean) => {
    if (chargingStationDate) {
      dispatch(
        setSelectedStation({
          id: chargingStationDate?.chargingStationId,
          name: chargingStationDate?.chargingStation,
        }),
      );
      if (newTab) {
        const url = `/ev-admin/transaction-management/station/${chargingStationDate.chargingStationId}/station-dashboard?searchBy=${chargingStationDate.chargingStation}`;
        window.open(url, '_blank'); // opens in a new tab
      } else {
        navigate({
          pathname: `/ev-admin/transaction-management/station/${chargingStationDate.chargingStationId}/station-dashboard`,
          search: `?searchBy=${chargingStationDate.chargingStation}`,
        });
      }
    } else {
      console.log('Error while opening device in separate page');
    }
  };

  /**
   * Function to open side panel on row click
   */
  const handleOpenSidePanelOnRowClick = (id: string) => {
    const updateParameters = new URLSearchParams(searchParameters);
    updateParameters.set('transactionObjectId', id);
    setSearchParameters(updateParameters);
  };
  const column = [
    {
      label: t('date'),
      accessorKey: 'createdAt' as keyof AvailableTransactions,
      sortAllow: true,
      /**
       * Date view
       * @param transaction
       */
      customData: (transaction: AvailableTransactions) => (
        <div className="table-row-each-data">{transaction.date}</div>
      ),
    },
    {
      label: t('time'),
      accessorKey: 'time' as keyof AvailableTransactions,
      sortAllow: true,
      /**
       * Time view
       * @param transaction
       */
      customData: (transaction: AvailableTransactions) => (
        <div className="table-row-each-data">{transaction.time}</div>
      ),
    },
    {
      label: t('transactionId'),
      accessorKey: '_id' as keyof AvailableTransactions,
      sortAllow: false,
    },
    {
      label: t('stationName'),
      accessorKey: 'chargingStation' as keyof AvailableTransactions,
      sortAllow: false,
      queryParamsKey: 'stationId',
      queryParamsValue: 'chargingStationId',
      sidePanelComponent: <StationViewSidePanel fetchIdFrom="queryParams" />,
      handleClickExpandView: handleOpenModelView,
    },
    {
      label: t('deviceId'),
      accessorKey: 'chargingPointSn' as keyof AvailableTransactions,
      sortAllow: false,
      queryParamsKey: 'deviceId',
      queryParamsValue: 'chargingPointSn',
      sidePanelComponent: <DeviceViewSidePanel fetchIdFrom="queryParams" />,
    },
    {
      label: t('userName'),
      accessorKey: 'endConsumer' as keyof AvailableTransactions,
      sortAllow: false,
      queryParamsKey: 'userCustomId',
      queryParamsValue: 'userCustomId',
      sidePanelComponent: <EndConsumerSidePanel fetchIdFrom="queryParams" />,
    },
    {
      label: t('powerConsumed'),
      accessorKey: 'powerConsumed' as keyof AvailableTransactions,
      sortAllow: true,
      /**
       * Amount view in table
       * @param transaction
       * @param index
       */
      customData: (transaction: AvailableTransactions) => (
        <div className="amount-in-transaction-table">
          <p className="table-row-each-data">
            {Number.isNaN(parseFloat(transaction.powerConsumed))
              ? '0.00'
              : parseFloat(transaction.powerConsumed).toFixed(2)}
          </p>
          <span>{powerUnit.KWH}</span>
        </div>
      ),
    },
    {
      label: t('totalAmount'),
      accessorKey: 'walletHistory' as keyof AvailableTransactions,
      sortAllow: true,
      /**
       * Amount view in table
       * @param transaction
       * @param index
       */
      customData: (transaction: AvailableTransactions) => (
        <div className="amount-in-transaction-table">
          <p className="table-row-each-data">
            {ToFixedValue(Number.isNaN(parseFloat(transaction.walletHistory))
              ? '0.00'
              : parseFloat(transaction.walletHistory).toFixed(2))}
          </p>
          <span dir="ltr">{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('revenueShare'),
      accessorKey: 'foRevenue' as keyof AvailableTransactions,
      sortAllow: true,
      /**
       * Amount view in table
       * @param transaction
       * @param index
       */
      customData: (transaction: AvailableTransactions) => (
        <div className="amount-in-transaction-table">
          <p className="table-row-each-data">
            {ToFixedValue(Number.isNaN(parseFloat(transaction.foRevenue))
              ? '0.00'
              : parseFloat(transaction.foRevenue).toFixed(2))}
          </p>
          <span dir="ltr">{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('paymentStatus'),
      accessorKey: 'paymentStatus' as keyof AvailableTransactions,
      sortAllow: false,
      hideWhenSidePanelOpen: true,
      /**
       * Payment status view in table
       * @param transaction
       * @param index
       */
      customData: (transaction: AvailableTransactions) => (
        <div className="status-in-transaction-table">
          <p
            className={`table-row-each-data ${transaction.paymentStatus === 'failed' ? 'error-text' : 'success-text'}`}>
            {CapitalizeFirstLetter(transaction.paymentStatus)}
          </p>
        </div>
      ),
    },
    {
      label: t('directStartTransaction'),
      accessorKey: 'directStartTransactionEvent' as keyof AvailableTransactions,
      hideWhenSidePanelOpen: false,
      /**
       * Data custom function
       * @param currentRow
       */
      customData: (currentRow: AvailableTransactions) => (
        <div>{currentRow?.directStartTransactionEvent ? 'Yes' : 'No'}</div>
      ),
    },
    {
      label: t('freeTransaction'),
      accessorKey: 'isFreeTransaction' as keyof AvailableTransactions,
      hideWhenSidePanelOpen: false,
      /**
       * Data custom function
       * @param currentRow
       */
      customData: (currentRow: AvailableTransactions) => (
        <div>{currentRow?.isFreeTransaction ? 'Yes' : 'No'}</div>
      ),
    },
    {
      label: t('rfidCard'),
      accessorKey: 'rfidNo' as keyof AvailableTransactions,
      hideWhenSidePanelOpen: false,
      /**
       * Data custom function
       * @param currentRow
       */
      customData: (currentRow: AvailableTransactions) => <div>{currentRow?.rfidNo}</div>,
    },
  ];
  /**
   * Function to handle page change
   * @param pageNumber
   */
  const handlePageChange = (pageNumber: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      sortField,
      sortBy,
      page: pageNumber,
      limit,
    });
  };

  /**
   * Function to handle data limit change
   */
  const handleLimitChange = (limitCount: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page: DefaultApiLimit.page,
      limit: limitCount,
      sortField,
      sortBy,
    });
  };

  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    if (Object.keys(sortData).length > 0) {
      dispatch(
        setTransactionFilterData({
          ...sortData,
        }),
      );
      dispatch(setSortData(sortData));
      fetchData({
        ...queryParameters,
        sortBy: sortData.sortBy,
        sortField:
          sortData.sortField === 'time' || sortData.sortField === 'date'
            ? 'createdAt'
            : sortData.sortField,
        page: DefaultApiLimit.page,
        limit,
      });
    }
  };

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('date'), accessorKey: 'createdAt' },
    { label: t('time'), accessorKey: 'time' },
    { label: t('transactionId'), accessorKey: '_id' },
    { label: t('stationName'), accessorKey: 'chargingStation' },
    { label: t('deviceId'), accessorKey: 'chargingPointSn' },
    { label: t('userName'), accessorKey: 'endConsumer' },
    { label: t('powerConsumed'), accessorKey: 'powerConsumed' },
    { label: t('totalAmount'), accessorKey: 'walletHistory' },
    { label: t('revenueShare'), accessorKey: 'foRevenue' },
    { label: t('paymentStatus'), accessorKey: 'paymentStatus' },
    { label: t('directStartTransaction'), accessorKey: 'directStartTransactionEvent' },
    { label: t('freeTransaction'), accessorKey: 'isFreeTransaction' },
    { label: t('rfidCard'), accessorKey: 'rfidNo' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    transactionApi
      .fetchTransactionExport({ ...queryParameters, sortField, sortBy })
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.results.map((row: AvailableTransactions, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'createdAt':
                return ConvertUTCtoDate(row?.createdAt) || 'N/A';
              case 'time':
                return FormatUtcString(row?.createdAt) || 'N/A';
              case 'powerConsumed':
                return `${ToFixedValue(row.powerConsumed)} kWh`;
              case 'walletHistory':
                return `${ToFixedValue(row.walletHistory)} ${t('currency')}`;
              case 'foRevenue':
                return `${ToFixedValue(row.foRevenue)} ${branding.text.currency}`;
              case 'paymentStatus':
                return CapitalizeFirstLetter(row.paymentStatus) || 'N/A';
              case 'directStartTransactionEvent':
                return row.directStartTransactionEvent ? 'Yes' : 'No';
              case 'isFreeTransaction':
                return row.isFreeTransaction ? 'Yes' : 'No';
              case 'rfidNo':
                return row.rfidNo || 'N/A';
              default:
                return (row as any)[col?.accessorKey] || 'N/A';
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'transaction-list.csv');
          } else {
            exportPDF(headers, rows, 'transaction-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortField, sortBy });
  };

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      limit: DefaultApiLimit.limit,
      page: DefaultApiLimit.page,
    });
  }, [currentFacilityTagsList, filters]);

  useEffect(() => {
    const hasSidePanelParameter = searchParameters.get('transactionId');
    if (hasSidePanelParameter) {
      dispatch(
        setPageTitle({
          title: t('transactions'),
          icon: <TransactionIcon />,
          count: totalResults,
        }),
      );
    } else {
      dispatch(
        setPageTitle({
          title: t('transactions'),
          icon: <TransactionIcon />,
          count: totalResults,
        }),
      );
    }
  }, [searchParameters, dispatch, t, totalResults]);

  return (
    <div className="transaction-management-wrap">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={modalBoxDetail.header}
      />
      <div className="table-container">
        <CustomTable
          dataKey="TransactionManagement"
          paginate
          loading={loading}
          limit={limit}
          header={column}
          data={transactions}
          currentPage={page}
          totalPages={totalPages}
          totalResults={totalResults}
          sortData={{
            sortBy,
            sortField,
          }}
          handleSortChange={handleSortFunction}
          handlePageChange={handlePageChange}
          handleLimitChange={handleLimitChange}
          exportDataPdfOrCSV={exportDataPdfOrCSV}
          exportLoader={exportLoader}
          topHeader={false}
          filters={filters}
          filtersToDispatch={updateTransactionFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListFilters}
          refreshRequired
          refreshCurrentPages={handleRefresh}
          hoverLabel={t('refresh')}
          serialNo
          flexDirection
          onHiddenColumnsChange={setHiddenColumns}
          rowClickEvent={handleOpenSidePanelOnRowClick}
          rowClickParamsKey="transactionObjectId"
          rowClickSidePanel={<TransactionSidePanel fetchIdFrom="queryParams" />}
        />
      </div>
    </div>
  );
}

export default TransactionManagement;
